import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import logo from "../../assets/icons/outclose_horizontal.svg";
import { NativeBackButton } from '../../components/ux/NativeBackButton';
import { ThemeToggle } from '@/components/ux/ThemeToggle';
import { LanguageSelector, useT, Trans } from '@/lib/i18n';
import tracking from '../../lib/tracking';

const CATEGORY_KEYS = [
  'howItWorks',
  'account',
  'payments',
  'matching',
  'reservations',
  'after',
] as const;

type CategoryKey = (typeof CATEGORY_KEYS)[number];

type Article = {
  q: string;
  p?: string[];
  pTrans?: string[];
  list?: { label: string; text: string }[];
  pAfter?: string[];
  note?: string;
};

const SUPPORT_EMAIL = 'team@outclose.com';

function EmailLink({ children }: { children?: React.ReactNode }) {
  return (
    <a
      href={`mailto:${SUPPORT_EMAIL}`}
      className="text-primary-500 hover:text-primary-600 underline underline-offset-2"
    >
      {children}
    </a>
  );
}

/**
 * Renders a translated string, swapping <email>…</email> tags for a real
 * mailto link. Keeps the <email> marker in translation files so translators
 * recognize it and so the URL isn't duplicated across locales.
 */
function renderWithEmailTag(text: string): React.ReactNode {
  const parts = text.split(/(<email>[\s\S]*?<\/email>)/g);
  return parts.map((part, i) => {
    const match = part.match(/^<email>([\s\S]*?)<\/email>$/);
    if (match) return <EmailLink key={i}>{match[1]}</EmailLink>;
    return <span key={i}>{part}</span>;
  });
}

function matchesQuery(article: Article, query: string): boolean {
  if (!query) return true;
  const haystack = [
    article.q,
    ...(article.p ?? []),
    ...(article.pTrans ?? []),
    ...(article.list?.flatMap((l) => [l.label, l.text]) ?? []),
    ...(article.pAfter ?? []),
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(query.toLowerCase());
}

function ArticleItem({ article }: { article: Article }) {
  return (
    <details className="group bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl px-5 open:border-primary-500 transition-colors">
      <summary className="py-4 cursor-pointer list-none flex items-center justify-between gap-4 font-Poppins font-medium text-type-900 dark:text-neutral-100">
        <span>{article.q}</span>
        <span
          aria-hidden
          className="shrink-0 w-2.5 h-2.5 border-r-2 border-b-2 border-neutral-500 rotate-45 transition-transform group-open:-rotate-[135deg]"
        />
      </summary>
      <div className="pb-5 text-[0.95rem] leading-relaxed text-neutral-600 dark:text-neutral-300 space-y-3">
        {article.p?.map((text, i) => <p key={`p-${i}`}>{text}</p>)}
        {article.list && (
          <ul className="list-disc pl-5 space-y-1.5">
            {article.list.map((item, i) => (
              <li key={`li-${i}`}>
                <strong className="text-type-900 dark:text-neutral-100">{item.label}</strong>{' '}
                {item.text}
              </li>
            ))}
          </ul>
        )}
        {article.pAfter?.map((text, i) => <p key={`pa-${i}`}>{text}</p>)}
        {article.pTrans?.map((text, i) => (
          <p key={`pt-${i}`}>{renderWithEmailTag(text)}</p>
        ))}
        {article.note && (
          <p className="text-xs text-neutral-500 dark:text-neutral-400">{article.note}</p>
        )}
      </div>
    </details>
  );
}

export default function Help() {
  const { t, ready, language } = useT('help');
  const [query, setQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | CategoryKey>('all');

  useEffect(() => {
    tracking.trackPage('help');
  }, []);

  // Pull all article arrays through i18next's returnObjects
  const articlesByCategory = useMemo(() => {
    if (!ready) return {} as Record<CategoryKey, Article[]>;
    const result = {} as Record<CategoryKey, Article[]>;
    for (const key of CATEGORY_KEYS) {
      const raw = t(`articles.${key}`, { returnObjects: true }) as unknown;
      result[key] = Array.isArray(raw) ? (raw as Article[]) : [];
    }
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- language forces recomputation when locale changes
  }, [ready, t, language]);

  if (!ready) return null;

  const filteredByCategory = CATEGORY_KEYS.map((key) => {
    const articles = articlesByCategory[key] ?? [];
    const categoryMatches = activeFilter === 'all' || activeFilter === key;
    const matching = categoryMatches
      ? articles.filter((a) => matchesQuery(a, query))
      : [];
    return { key, articles: matching };
  });

  const totalMatches = filteredByCategory.reduce((n, c) => n + c.articles.length, 0);

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 text-type-900 dark:text-white">
      <div className="max-w-4xl mx-auto px-4 pt-12 pb-20">
        <NativeBackButton className="mb-4" />
        <div className="mb-10 flex items-center justify-between">
          <a href="https://outclose.com"><img src={logo} alt="Outclose" className="w-auto h-6 dark:invert" /></a>
          <div className="flex items-center gap-2">
            <LanguageSelector className="dark:text-neutral-100 dark:border-neutral-600" />
            <ThemeToggle />
          </div>
        </div>

        {/* Hero */}
        <div className="text-center mb-10">
          <h1 className="font-Poppins text-4xl md:text-5xl font-semibold mb-3">
            {t('hero.title')}
          </h1>
          <p className="text-neutral-600 dark:text-neutral-300 max-w-xl mx-auto mb-6">
            {t('hero.subtitle')}
          </p>
          <div className="relative max-w-xl mx-auto">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"
              aria-hidden
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('hero.searchPlaceholder')}
              className="w-full pl-11 pr-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-type-900 dark:text-neutral-100 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Category filter pills */}
        <div className="flex flex-wrap gap-2 justify-center mb-8">
          <button
            onClick={() => setActiveFilter('all')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              activeFilter === 'all'
                ? 'bg-primary-500 text-white border-primary-500'
                : 'bg-white dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700 hover:border-primary-500'
            }`}
          >
            {t('filters.all')}
          </button>
          {CATEGORY_KEYS.map((key) => (
            <button
              key={key}
              onClick={() => setActiveFilter(key)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                activeFilter === key
                  ? 'bg-primary-500 text-white border-primary-500'
                  : 'bg-white dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700 hover:border-primary-500'
              }`}
            >
              {t(`categories.${key}.title`)}
            </button>
          ))}
        </div>

        {/* Articles */}
        {totalMatches === 0 ? (
          <div className="text-center text-neutral-500 dark:text-neutral-400 py-12">
            <Trans
              i18nKey="search.noResults"
              ns="help"
              components={{ email: <EmailLink /> }}
            />
          </div>
        ) : (
          <div className="space-y-10">
            {filteredByCategory.map(
              ({ key, articles }) =>
                articles.length > 0 && (
                  <section key={key} id={key} className="scroll-mt-6">
                    <h2 className="font-Poppins text-2xl font-semibold mb-4">
                      {t(`categories.${key}.title`)}
                    </h2>
                    <div className="flex flex-col gap-2">
                      {articles.map((article, i) => (
                        <ArticleItem key={`${key}-${i}`} article={article} />
                      ))}
                    </div>
                  </section>
                ),
            )}
          </div>
        )}

        {/* Contact */}
        <section className="mt-16 rounded-3xl p-10 text-center text-white bg-gradient-to-br from-primary-500 to-secondary-500">
          <h2 className="font-Poppins text-2xl md:text-3xl font-semibold mb-2">
            {t('contact.title')}
          </h2>
          <p className="opacity-95 max-w-xl mx-auto mb-6">{t('contact.subtitle')}</p>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="inline-block bg-white text-grape-700 font-semibold px-7 py-3 rounded-full hover:-translate-y-0.5 transition-transform"
          >
            {t('contact.cta')}
          </a>
          <span className="block mt-4 text-sm text-white/90">
            {t('contact.or')}{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">
              {SUPPORT_EMAIL}
            </a>
          </span>
        </section>
      </div>
    </div>
  );
}
